import React, { useEffect, useState } from 'react';
import useApi from '../api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Edit, Trash2, Loader2, Webhook, Code, ExternalLink } from "lucide-react";
import Upgrade from '../modals/Upgrade';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import WebhookModal from './WebhookModal';
import { useUser } from "../user/UserContext";
import { LicenseType } from '../types';
import { WebhookEvents } from './webhookEvents';

interface WebhookConfig {
    id: string;
    name: string;
    target_url: string;
    auth_secret?: string;
    has_auth_secret?: boolean;
    is_active: boolean;
    events: WebhookEvents;
    created_at: string;
    updated_at: string;
}

const ALLOWED_LICENSES = [LicenseType.BUSINESS, LicenseType.AIMDOC];

const Webhooks: React.FC = () => {
    const { get, post, put, remove } = useApi();
    const { organization, checkLicenseType } = useUser();
    const navigate = useNavigate();
    
    // Access control
    const [hasAccess, setHasAccess] = useState<boolean>(false);
    const [checkingLicense, setCheckingLicense] = useState<boolean>(true);
    const [upgradeModalVisible, setUpgradeModalVisible] = useState<boolean>(false);
    
    // Webhook management states
    const [webhooks, setWebhooks] = useState<WebhookConfig[]>([]);
    const [webhookModalOpen, setWebhookModalOpen] = useState<boolean>(false);
    const [editingWebhook, setEditingWebhook] = useState<WebhookConfig | null>(null);
    const [loadingWebhooks, setLoadingWebhooks] = useState<boolean>(false);
    const [savingWebhook, setSavingWebhook] = useState<boolean>(false);
    const [deletingWebhookId, setDeletingWebhookId] = useState<string | null>(null);

    // Webhook management functions
    const fetchWebhooks = async () => {
        setLoadingWebhooks(true);
        try {
            const response = await get('/hooks/external');
            setWebhooks(response.data);
        } catch (error) {
            console.error('Failed to fetch webhooks:', error);
            // Will get 404 initially since endpoint doesn't exist yet
        } finally {
            setLoadingWebhooks(false);
        }
    };

    const handleCreateWebhook = () => {
        setEditingWebhook(null);
        setWebhookModalOpen(true);
    };

    const handleDeleteWebhook = async (webhookId: string) => {
        setDeletingWebhookId(webhookId);
        try {
            await remove(`/hooks/external/${webhookId}`);
            toast.success('Webhook deleted successfully');
            await fetchWebhooks();
        } catch (error) {
            toast.error('Failed to delete webhook');
            console.error('Failed to delete webhook:', error);
        } finally {
            setDeletingWebhookId(null);
        }
    };

    const handleSaveWebhook = async (webhookData: Partial<WebhookConfig>) => {
        setSavingWebhook(true);
        try {
            let savedWebhook = null;
            if (editingWebhook) {
                const response = await put(`/hooks/external/${editingWebhook.id}`, webhookData);
                savedWebhook = response.data;
                toast.success('Webhook updated successfully');
            } else {
                const response = await post('/hooks/external', webhookData);
                savedWebhook = response.data;
                toast.success('Webhook created successfully');
            }
            setWebhookModalOpen(false);
            await fetchWebhooks();
            return savedWebhook;
        } catch (error) {
            toast.error('Failed to save webhook');
            console.error('Failed to save webhook:', error);
            return null;
        } finally {
            setSavingWebhook(false);
        }
    };

    // Check license access on mount and when organization changes
    useEffect(() => {
        if (organization) {
            const access = ALLOWED_LICENSES.some(license => 
                checkLicenseType(license)
            );
            setHasAccess(access);
            setCheckingLicense(false);
            
            // Only fetch webhooks if user has access
            if (access && organization.id) {
                fetchWebhooks();
            }
        }
    }, [organization, checkLicenseType]);

    // Show loading while checking license
    if (checkingLicense) {
        return (
            <div className="flex justify-center items-center min-h-[350px]">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    // Show upgrade screen if no access
    if (!hasAccess) {
        return (
            <>
                {/* Upgrade Modal */}
                <Upgrade 
                    showModal={upgradeModalVisible} 
                    setShowModal={setUpgradeModalVisible}
                    sessionId={null}
                />

                {/* Centered upgrade card */}
                <div className="relative">
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                        <Card className="max-w-md bg-background">
                            <CardHeader className="text-center">
                                <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                                    <Webhook className="h-8 w-8 text-primary" />
                                </div>
                                <CardTitle className="text-xl">Webhooks</CardTitle>
                            </CardHeader>
                            <CardContent className="text-center space-y-4">
                                <p className="text-muted-foreground">
                                    Integrate Aimdoc with your existing tools and automate workflows with custom webhooks.
                                </p>
                                <div className="space-y-2 text-sm text-left">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                                        <span>Custom webhook endpoints</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-green-500 rounded-full" />
                                        <span>Real-time event notifications</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-purple-500 rounded-full" />
                                        <span>Send data to external systems</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-orange-500 rounded-full" />
                                        <span>Test webhook payloads</span>
                                    </div>
                                </div>
                                <Button 
                                    onClick={() => setUpgradeModalVisible(true)}
                                    className="w-full"
                                    size="lg"
                                >
                                    Upgrade to Convert
                                </Button>
                                <p className="text-xs text-muted-foreground">
                                    Available on the Convert plan
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Blurred background content */}
                    <div className="blur-sm pointer-events-none opacity-50">
                        {/* Webhooks Table */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Webhooks</CardTitle>
                                <CardDescription>Configure webhook endpoints to receive real-time events</CardDescription>
                            </CardHeader>
                            <CardContent className="min-h-[350px]">
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            {/* Webhooks Table */}
            <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div className="space-y-1.5">
                                <CardTitle className="flex items-center gap-2">
                                    <Webhook className="h-5 w-5" />
                                    Webhooks
                                </CardTitle>
                                <CardDescription>Configure webhooks to receive event notifications</CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button 
                                    variant="outline" 
                                    onClick={() => window.open('https://api.aimdoc.ai/api/v1/redoc#tag/Webhooks', '_blank')}
                                >
                                    <ExternalLink className="h-4 w-4 mr-2" />
                                    View Webhook Docs
                                </Button>
                                <Button onClick={handleCreateWebhook}>
                                    <Plus className="h-4 w-4 mr-1" />
                                    Add Webhook
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {loadingWebhooks ? (
                            <div className="flex justify-center items-center h-[350px]">
                                <Loader2 className="h-8 w-8 animate-spin" />
                            </div>
                        ) : webhooks.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-[350px] text-center">
                                <div className="bg-muted/50 rounded-full p-6 mb-4">
                                    <Webhook className="h-12 w-12 text-muted-foreground" />
                                </div>
                                <h3 className="text-lg font-semibold mb-2">No webhooks yet</h3>
                                <p className="text-sm text-muted-foreground max-w-sm mb-6">
                                    Create your first webhook to start receiving real-time event notifications
                                </p>
                                <Button onClick={handleCreateWebhook}>
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Your First Webhook
                                </Button>
                            </div>
                        ) : (
                            <div className="min-h-[350px]">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Events</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                    {(
                                        webhooks.map((webhook) => {
                                            const activeEvents = Object.entries(webhook.events)
                                                .filter(([_, enabled]) => enabled)
                                                .map(([event]) => event.replace(/_/g, ' '))
                                                .map(s => s.charAt(0).toUpperCase() + s.slice(1));
                                            
                                            return (
                                                <TableRow key={webhook.id}>
                                                    <TableCell className="font-medium">
                                                        <div className="flex items-center gap-2">
                                                            <span>{webhook.name}</span>
                                                            {webhook.has_auth_secret && (
                                                                <Badge variant="outline">Auth</Badge>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant={webhook.is_active ? "default" : "secondary"}>
                                                            {webhook.is_active ? "Active" : "Inactive"}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="text-sm text-muted-foreground">
                                                            {activeEvents.length} event{activeEvents.length !== 1 ? 's' : ''}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex justify-end gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => navigate(`/integrations/webhooks/${webhook.id}`)}
                                                            >
                                                                <Edit className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleDeleteWebhook(webhook.id)}
                                                                disabled={deletingWebhookId === webhook.id}
                                                            >
                                                                {deletingWebhookId === webhook.id ? (
                                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                                ) : (
                                                                    <Trash2 className="h-4 w-4" />
                                                                )}
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            
            {/* Webhook Modal */}
            <WebhookModal
                open={webhookModalOpen}
                onOpenChange={setWebhookModalOpen}
                webhook={editingWebhook}
                onSave={handleSaveWebhook}
                saving={savingWebhook}
            />
            
            {/* Upgrade Modal */}
            <Upgrade 
                showModal={upgradeModalVisible} 
                setShowModal={setUpgradeModalVisible}
                sessionId={null}
            />
        </>
    );
};

export default Webhooks;
